import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { searchTranscriptions, searchViralContent } from '../lib/api';
import { TranscriptionResponse } from '../lib/types';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Search as SearchIcon, FileText, TrendingUp, Filter, AlertCircle, ThumbsUp, Eye, Clock, Loader2, ArrowRight, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { cn } from '../lib/utils';
export function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  const initialMode = searchParams.get('mode') as 'regular' | 'viral' || 'regular';
  const [query, setQuery] = useState(initialQuery);
  const [searchMode, setSearchMode] = useState<'regular' | 'viral'>(initialMode);
  const [results, setResults] = useState<TranscriptionResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [minLikes, setMinLikes] = useState(50);
  const [minViews, setMinViews] = useState(1000);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const resultsPerPage = 10;
  // Update URL when search parameters change
  useEffect(() => {
    if (initialQuery) {
      performSearch(initialQuery, initialMode);
    }
  }, [initialQuery, initialMode]);
  const performSearch = async (searchQuery: string, mode: 'regular' | 'viral', pageNum: number = 1) => {
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const offset = (pageNum - 1) * resultsPerPage;
      let searchResults: TranscriptionResponse[];
      if (mode === 'viral') {
        searchResults = await searchViralContent(searchQuery, minLikes, minViews, resultsPerPage + 1 // Request one extra to check if there are more
        );
      } else {
        searchResults = await searchTranscriptions(searchQuery, resultsPerPage + 1,
        // Request one extra to check if there are more
        offset);
      }
      // Check if there are more results
      setHasMore(searchResults.length > resultsPerPage);
      // Remove the extra item if we have more
      if (searchResults.length > resultsPerPage) {
        searchResults = searchResults.slice(0, resultsPerPage);
      }
      setResults(searchResults);
      setPage(pageNum);
    } catch (err) {
      console.error('Search error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred while searching. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const newParams = new URLSearchParams();
    newParams.set('q', query);
    newParams.set('mode', searchMode);
    setSearchParams(newParams);
    performSearch(query, searchMode, 1);
  };
  const handleNextPage = () => {
    if (hasMore) {
      performSearch(query, searchMode, page + 1);
    }
  };
  const handlePrevPage = () => {
    if (page > 1) {
      performSearch(query, searchMode, page - 1);
    }
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  return <div className="container py-8" data-id="element-1825">
      <div className="mb-8" data-id="element-1826">
        <h1 className="text-3xl font-bold mb-2" data-id="element-1827">Search Transcriptions</h1>
        <p className="text-muted-foreground" data-id="element-1828">
          Find content across all transcribed videos with our powerful search
        </p>
      </div>
      {/* Search Form */}
      <form onSubmit={handleSearch} className="mb-6" data-id="element-1829">
        <div className="flex flex-col sm:flex-row gap-3" data-id="element-1830">
          <div className="relative flex-grow" data-id="element-1831">
            <SearchIcon size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" data-id="element-1832" />
            <Input type="text" placeholder="Search for keywords, phrases, or topics..." value={query} onChange={e => setQuery(e.target.value)} className="pl-10" data-id="element-1833" />
          </div>
          <div className="flex gap-2" data-id="element-1834">
            <Button type="submit" disabled={isLoading || !query.trim()} className="min-w-[100px]" data-id="element-1835">
              {isLoading ? <Loader2 size={16} className="mr-2 animate-spin" data-id="element-1836" /> : <SearchIcon size={16} className="mr-2" data-id="element-1837" />}
              Search
            </Button>
            <Button type="button" variant="outline" onClick={() => setShowFilters(!showFilters)} data-id="element-1838">
              <Filter size={16} className="mr-2" data-id="element-1839" />
              Filters
            </Button>
          </div>
        </div>
        {/* Search Modes and Filters */}
        <div className={cn('mt-4', !showFilters && 'sm:flex items-center justify-between')} data-id="element-1840">
          <div className="flex flex-wrap gap-2 mb-4 sm:mb-0" data-id="element-1841">
            <Button type="button" variant={searchMode === 'regular' ? 'default' : 'outline'} size="sm" onClick={() => setSearchMode('regular')} className="flex items-center" data-id="element-1842">
              <FileText size={14} className="mr-1" data-id="element-1843" />
              Regular Search
            </Button>
            <Button type="button" variant={searchMode === 'viral' ? 'default' : 'outline'} size="sm" onClick={() => setSearchMode('viral')} className="flex items-center" data-id="element-1844">
              <TrendingUp size={14} className="mr-1" data-id="element-1845" />
              Viral Content
            </Button>
          </div>
          {/* Advanced Filters (conditionally shown) */}
          {showFilters && <div className="bg-secondary/20 p-4 rounded-lg space-y-4" data-id="element-1846">
              <h3 className="font-medium mb-2 text-sm" data-id="element-1847">Advanced Filters</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1848">
                <div data-id="element-1849">
                  <label className="block text-sm mb-1" data-id="element-1850">Minimum Likes</label>
                  <div className="flex items-center" data-id="element-1851">
                    <ThumbsUp size={14} className="text-muted-foreground mr-2" data-id="element-1852" />
                    <Input type="number" min="0" value={minLikes} onChange={e => setMinLikes(Number(e.target.value))} disabled={searchMode !== 'viral'} className="max-w-[150px]" data-id="element-1853" />
                  </div>
                </div>
                <div data-id="element-1854">
                  <label className="block text-sm mb-1" data-id="element-1855">Minimum Views</label>
                  <div className="flex items-center" data-id="element-1856">
                    <Eye size={14} className="text-muted-foreground mr-2" data-id="element-1857" />
                    <Input type="number" min="0" value={minViews} onChange={e => setMinViews(Number(e.target.value))} disabled={searchMode !== 'viral'} className="max-w-[150px]" data-id="element-1858" />
                  </div>
                </div>
              </div>
              <p className="text-xs text-muted-foreground" data-id="element-1859">
                Note: Engagement filters only apply to Viral Content search mode
              </p>
            </div>}
        </div>
      </form>
      {/* Search Results */}
      {error ? <Card className="border-destructive/20 bg-destructive/5" data-id="element-1860">
          <CardContent className="p-6" data-id="element-1861">
            <div className="flex items-start" data-id="element-1862">
              <AlertCircle className="text-destructive mr-3 mt-0.5" size={20} data-id="element-1863" />
              <div data-id="element-1864">
                <h3 className="font-medium mb-1" data-id="element-1865">Search Error</h3>
                <p className="text-muted-foreground" data-id="element-1866">{error}</p>
              </div>
            </div>
          </CardContent>
        </Card> : isLoading ? <div className="space-y-4" data-id="element-1867">
          {[...Array(3)].map((_, i) => <Card key={i} className="animate-pulse" data-id="element-1868">
              <CardContent className="p-4" data-id="element-1869">
                <div className="h-5 bg-secondary/60 rounded w-3/4 mb-3" data-id="element-1870"></div>
                <div className="h-3 bg-secondary/40 rounded w-1/2 mb-2" data-id="element-1871"></div>
                <div className="h-3 bg-secondary/40 rounded w-full mb-2" data-id="element-1872"></div>
                <div className="h-3 bg-secondary/40 rounded w-4/5" data-id="element-1873"></div>
              </CardContent>
            </Card>)}
        </div> : results.length > 0 ? <div data-id="element-1874">
          <p className="text-sm text-muted-foreground mb-4" data-id="element-1875">
            Showing {results.length} results for "{query}"
            {searchMode === 'viral' && ' (viral content)'}
          </p>
          <div className="space-y-4 mb-6" data-id="element-1876">
            {results.map(result => <Link key={result.task_id} to={`/app/transcriptions/${result.task_id}`} className="block" data-id="element-1877">
                <Card className="overflow-hidden hover:border-primary/50 hover:shadow-sm transition-all" data-id="element-1878">
                  <CardContent className="p-0" data-id="element-1879">
                    <div className="flex flex-col md:flex-row" data-id="element-1880">
                      {/* Thumbnail if available */}
                      {(result.thumbnail_url || result.thumbnail || result.square_thumbnail_url) && <div className="w-full md:w-48 h-32 flex-shrink-0" data-id="element-1881">
                          <img src={result.square_thumbnail_url || result.thumbnail_url || result.thumbnail} alt={result.title || 'Video thumbnail'} className="w-full h-full object-cover" data-id="element-1882" />
                        </div>}
                      <div className="p-4 flex-grow" data-id="element-1883">
                        <h3 className="font-medium mb-1 line-clamp-1" data-id="element-1884">
                          {result.title || 'Untitled Transcription'}
                        </h3>
                        {/* Show quote if available */}
                        {result.quote && <div className="mb-2 text-sm italic text-muted-foreground line-clamp-2" data-id="element-1885">
                            "{result.quote}"
                          </div>}
                        {/* Show TLDR if available */}
                        {result.tldr && result.tldr.length > 0 && <div className="mb-2" data-id="element-1886">
                            <div className="flex items-center gap-1 text-xs font-medium text-primary mb-1" data-id="element-1887">
                              <Sparkles size={12} data-id="element-1888" />
                              <span data-id="element-1889">Key points:</span>
                            </div>
                            <ul className="text-xs text-muted-foreground space-y-1 line-clamp-2" data-id="element-1890">
                              {result.tldr.slice(0, 2).map((point, i) => <li key={i} className="flex items-start" data-id="element-1891">
                                  <span className="mr-1" data-id="element-1892">•</span>
                                  <span className="line-clamp-1" data-id="element-1893">{point}</span>
                                </li>)}
                            </ul>
                          </div>}
                        {/* Metadata */}
                        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground mt-2" data-id="element-1894">
                          <span className="flex items-center" data-id="element-1895">
                            <Clock size={12} className="mr-1" data-id="element-1896" />
                            {formatDate(result.created_at)}
                          </span>
                          {result.view_count !== undefined && <span className="flex items-center" data-id="element-1897">
                              <Eye size={12} className="mr-1" data-id="element-1898" />
                              {result.view_count.toLocaleString()} views
                            </span>}
                          {result.like_count !== undefined && <span className="flex items-center" data-id="element-1899">
                              <ThumbsUp size={12} className="mr-1" data-id="element-1900" />
                              {result.like_count.toLocaleString()} likes
                            </span>}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>)}
          </div>
          {/* Pagination */}
          {(page > 1 || hasMore) && <div className="flex justify-between items-center" data-id="element-1901">
              <Button variant="outline" onClick={handlePrevPage} disabled={page <= 1} size="sm" data-id="element-1902">
                <ChevronLeft size={16} className="mr-1" data-id="element-1903" />
                Previous
              </Button>
              <span className="text-sm text-muted-foreground" data-id="element-1904">Page {page}</span>
              <Button variant="outline" onClick={handleNextPage} disabled={!hasMore} size="sm" data-id="element-1905">
                Next
                <ChevronRight size={16} className="ml-1" data-id="element-1906" />
              </Button>
            </div>}
        </div> : query ? <Card className="border-muted" data-id="element-1907">
          <CardContent className="p-8 text-center" data-id="element-1908">
            <SearchIcon size={48} className="mx-auto mb-4 text-muted-foreground/60" data-id="element-1909" />
            <h3 className="text-lg font-medium mb-2" data-id="element-1910">No results found</h3>
            <p className="text-muted-foreground mb-6" data-id="element-1911">
              We couldn't find any transcriptions matching "{query}"
              {searchMode === 'viral' && ' with the current engagement filters'}
            </p>
            {searchMode === 'viral' && <Button variant="outline" onClick={() => {
          setSearchMode('regular');
          performSearch(query, 'regular');
        }} data-id="element-1912">
                Try Regular Search
              </Button>}
          </CardContent>
        </Card> : null}
    </div>;
}